import { useState, useMemo } from 'react';
import { useAuth } from 'wasp/client/auth';
import {
  getPaginatedUsers,
  updateUserPlatformRole,
  assignModeratorRole,
  revokeModeratorRole,
  useQuery,
} from 'wasp/client/operations';
import toast from 'react-hot-toast';
import { Shield, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import LoadingSpinner from '../layout/LoadingSpinner';
import Breadcrumb from '../layout/Breadcrumb';
import {
  isAdministrator,
  isPlatformOwner,
  getRoleBadgeColor,
  getRoleLabel,
} from '../../client/lib/platformRole';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

type RoleUser = {
  id: string;
  email: string | null;
  username: string | null;
  fullName: string | null;
  platformRole: string;
  createdAt: Date;
};

type PendingAction = {
  user: RoleUser;
  newRole: 'USER' | 'MODERATOR' | 'ADMINISTRATOR';
  label: string;
};

const UserRolesPage = () => {
  const { data: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'user' | 'email' | 'role' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const { data: usersData, isLoading, refetch } = useQuery(getPaginatedUsers, {
    skipPages: 0,
    pageSize: 500,
    filter: {
      ...(search && { emailContains: search }),
    },
  });

  const users: RoleUser[] = useMemo(() => {
    const list = (usersData as any)?.users ?? [];
    // Filter out PLATFORM_OWNER unless current user is also PLATFORM_OWNER
    if (!isPlatformOwner(currentUser)) {
      return list.filter((u: RoleUser) => u.platformRole !== 'PLATFORM_OWNER');
    }
    return list;
  }, [usersData, currentUser]);

  const sorted = useMemo(() => {
    if (!sortField) return users;
    return [...users].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortField === 'user') { aVal = (a.fullName || a.username || '').toLowerCase(); bVal = (b.fullName || b.username || '').toLowerCase(); }
      if (sortField === 'email') { aVal = (a.email || '').toLowerCase(); bVal = (b.email || '').toLowerCase(); }
      if (sortField === 'role') { aVal = a.platformRole; bVal = b.platformRole; }
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [users, sortField, sortDir]);

  const handleSort = (field: 'user' | 'email' | 'role') => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: 'user' | 'email' | 'role' }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />;
  };

  const requestRoleChange = (user: RoleUser, newRole: 'USER' | 'MODERATOR' | 'ADMINISTRATOR') => {
    const labels = { USER: 'User', MODERATOR: 'Moderator', ADMINISTRATOR: 'Administrator' };
    setPendingAction({ user, newRole, label: labels[newRole] });
  };

  const confirmRoleChange = async () => {
    if (!pendingAction) return;
    const { user, newRole } = pendingAction;
    setProcessingId(user.id);
    try {
      if (newRole === 'MODERATOR' && user.platformRole === 'USER') {
        await assignModeratorRole({ userId: user.id });
      } else if (newRole === 'USER' && user.platformRole === 'MODERATOR') {
        await revokeModeratorRole({ userId: user.id });
      } else {
        await updateUserPlatformRole({ userId: user.id, platformRole: newRole });
      }
      toast.success(`${user.email || user.username} is now ${pendingAction.label}`);
      setPendingAction(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update role');
    } finally {
      setProcessingId(null);
    }
  };

  if (!isAdministrator(currentUser)) {
    return (
      <div className="p-8 text-center text-gray-500">Access denied. Administrator privileges required.</div>
    );
  }

  return (
    <div className="w-full">
      <Breadcrumb pageName="User Roles" />

      <div className="flex flex-col gap-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Roles</h1>
            <p className="text-sm text-gray-500 mt-1">Manage platform roles for all users</p>
          </div>
        </div>

        {/* Role Legend */}
        <div className="flex flex-wrap gap-3 p-4 bg-muted rounded-lg text-sm">
          {(['USER', 'MODERATOR', 'ADMINISTRATOR'] as const).map(role => (
            <div key={role} className="flex items-center gap-2">
              <Badge className={getRoleBadgeColor(role)}>{getRoleLabel(role)}</Badge>
              <span className="text-muted-foreground">
                {role === 'USER' && '— default, own content only'}
                {role === 'MODERATOR' && '— moderate assigned areas'}
                {role === 'ADMINISTRATOR' && '— full admin access'}
              </span>
            </div>
          ))}
        </div>

        {/* Search */}
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />

        {/* Table */}
        <div className="border border-border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-muted px-4 py-3 grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider text-muted-foreground select-none">
            <div className="col-span-4 flex items-center cursor-pointer" onClick={() => handleSort('user')}>
              User <SortIcon field="user" />
            </div>
            <div className="col-span-4 flex items-center cursor-pointer" onClick={() => handleSort('email')}>
              Email <SortIcon field="email" />
            </div>
            <div className="col-span-2 flex items-center cursor-pointer" onClick={() => handleSort('role')}>
              Role <SortIcon field="role" />
            </div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Rows */}
          {isLoading ? (
            <div className="p-8 flex justify-center"><LoadingSpinner /></div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No users found</div>
          ) : (
            sorted.map(user => {
              const isSelf = (currentUser as any)?.id === user.id;
              const isOwner = user.platformRole === 'PLATFORM_OWNER';
              const isAdmin = user.platformRole === 'ADMINISTRATOR';
              const isMod = user.platformRole === 'MODERATOR';
              const isUser = user.platformRole === 'USER';
              const canModify = !isSelf && !isOwner && !(isAdmin && !isPlatformOwner(currentUser));

              return (
                <div key={user.id} className="px-4 py-3 grid grid-cols-12 gap-4 items-center border-t border-border hover:bg-muted/50 transition-colors">
                  <div className="col-span-4">
                    <div className="text-sm font-medium">{user.fullName || user.username || '—'}</div>
                    {user.username && user.fullName && <div className="text-xs text-muted-foreground">@{user.username}</div>}
                  </div>
                  <div className="col-span-4 text-sm text-muted-foreground truncate">{user.email || '—'}</div>
                  <div className="col-span-2">
                    <Badge className={getRoleBadgeColor(user.platformRole as any)}>{getRoleLabel(user.platformRole as any)}</Badge>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    {canModify && (
                      <>
                        {isUser && (
                          <Button size="sm" variant="outline" disabled={processingId === user.id}
                            onClick={() => requestRoleChange(user, 'MODERATOR')}
                            title="Promote to Moderator">
                            <ChevronUp className="h-3 w-3 mr-1" /> Mod
                          </Button>
                        )}
                        {isMod && (
                          <>
                            <Button size="sm" variant="outline" disabled={processingId === user.id}
                              onClick={() => requestRoleChange(user, 'USER')}
                              title="Demote to User">
                              <ChevronDown className="h-3 w-3 mr-1" /> User
                            </Button>
                            {isPlatformOwner(currentUser) && (
                              <Button size="sm" variant="outline" disabled={processingId === user.id}
                                onClick={() => requestRoleChange(user, 'ADMINISTRATOR')}
                                title="Promote to Administrator">
                                <ChevronUp className="h-3 w-3 mr-1" /> Admin
                              </Button>
                            )}
                          </>
                        )}
                        {isAdmin && isPlatformOwner(currentUser) && (
                          <Button size="sm" variant="outline" disabled={processingId === user.id}
                            onClick={() => requestRoleChange(user, 'MODERATOR')}
                            title="Demote to Moderator">
                            <ChevronDown className="h-3 w-3 mr-1" /> Mod
                          </Button>
                        )}
                      </>
                    )}
                    {isSelf && <span className="text-xs text-muted-foreground italic">You</span>}
                    {isOwner && <Shield className="h-4 w-4 text-purple-500" aria-label="Platform Owner" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      <AlertDialog open={!!pendingAction} onOpenChange={(open) => { if (!open) setPendingAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Role to {pendingAction?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Set <strong>{pendingAction?.user.email || pendingAction?.user.username}</strong>'s role to <strong>{pendingAction?.label}</strong>. This will immediately change what they can access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRoleChange}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserRolesPage;



